/**
 * @file fetchDistrictsFile.ts
 * @description
 * Fetches the districts.json file for a given congress, which serves as the
 * source of truth for valid congressional district keys.
 *
 * Expected public file path:
 *   /public/data/congress{congressId}/districts.json
 * Example:
 *   /public/data/congress118/districts.json
 *
 * The districts.json file contains an array of district keys in
 * "congress-state-district" format (e.g., "118-al-01").
 */

import { asset } from '@/utils/asset';

/**
 * Fetches the districts.json file for a given congress.
 *
 * @param congressId - The congress number (e.g., 118)
 * @returns Promise resolving to an array of district keys in "congress-state-district" format
 * @throws Error if the HTTP request fails
 */
export async function fetchDistrictsFile(congressId: number): Promise<string[]> {
  const url = asset(`data/congress${congressId}/districts.json`);
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`[fetchDistrictsFile] HTTP ${res.status} for ${url}`);
  }

  const json: unknown = await res.json();

  // Validate that the response is an array
  if (!Array.isArray(json)) {
    throw new Error(`[fetchDistrictsFile] Expected array, got ${typeof json} for ${url}`);
  }

  // Validate that all elements are strings
  if (!json.every((item): item is string => typeof item === 'string')) {
    throw new Error(`[fetchDistrictsFile] Expected array of strings, got mixed types for ${url}`);
  }

  return json;
}
